import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useMagazineModal } from "../components/Nav";
import { Play, ArrowRight } from "lucide-react";

const reveal = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
};

const EXPERIENCE = [
  { key: "film", label: "Film" },
  { key: "magazine", label: "Magazine" },
  { key: "photography", label: "Photography" },
  { key: "behind", label: "Behind the Drive" },
];

export default function Nocturne() {
  const [editions, setEditions] = useState([]);
  const [featured, setFeatured] = useState(null);
  const { open } = useMagazineModal();

  useEffect(() => {
    (async () => {
      try {
        const [a, b] = await Promise.all([
          api.get("/editions"),
          api.get("/editions/featured"),
        ]);
        setEditions(a.data || []);
        setFeatured(b.data && b.data.id ? b.data : a.data?.[0] || null);
      } catch (_) {}
    })();
  }, []);

  const issueNumber = "Issue 01";
  const issueTitle = "The Art of Control";

  return (
    <main data-testid="nocturne-page" className="bg-black pt-32">
      {/* HERO */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 pb-16">
        <motion.p {...reveal} className="overline mb-6" data-testid="nocturne-eyebrow">
          Editorial Experience · Nocturne
        </motion.p>
        <motion.h1
          {...reveal}
          className="font-display text-[16vw] md:text-[12vw] leading-[0.9] text-white"
        >
          Good Bandit
          <br />
          <span className="italic text-[var(--gb-gold)]">Nocturne</span>
        </motion.h1>

        <motion.p
          {...reveal}
          className="text-white/70 text-base md:text-xl max-w-2xl mt-12 leading-relaxed"
          data-testid="nocturne-body"
        >
          An editorial experience where cinema, photography and publishing converge to
          tell stories of luxury automobiles.
        </motion.p>
        <motion.p
          {...reveal}
          className="text-white/45 text-sm md:text-base max-w-2xl mt-5 leading-relaxed italic"
        >
          One story told through film, photography, writing and design.
        </motion.p>
      </section>

      {/* EXPERIENCE NAVIGATION */}
      <section className="border-y border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-2 md:grid-cols-4">
          {EXPERIENCE.map((item, i) => (
            <a
              key={item.key}
              href={`#${item.key}`}
              data-testid={`exp-nav-${item.key}`}
              className={`group py-8 md:py-10 text-center transition-colors duration-500 ${
                i !== EXPERIENCE.length - 1 ? "md:border-r border-white/5" : ""
              } ${i < EXPERIENCE.length - 2 ? "border-b md:border-b-0 border-white/5" : ""}`}
            >
              <p className="kbd text-white/30 mb-3 group-hover:text-[var(--gb-gold)] transition-colors duration-500">
                0{i + 1}
              </p>
              <p className="font-display text-2xl md:text-3xl text-white group-hover:text-[var(--gb-gold)] transition-colors duration-500">
                {item.label}
              </p>
            </a>
          ))}
        </div>
      </section>

      {/* INTRODUCE NOCTURNE */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-32 md:py-40">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <motion.div {...reveal} className="md:col-span-5">
            <p className="overline mb-6">Introducing</p>
            <h2 className="font-display text-6xl md:text-8xl text-white leading-[0.92]">
              More than
              <br />
              <span className="italic text-[var(--gb-gold)]">a film.</span>
            </h2>
          </motion.div>
          <motion.div {...reveal} className="md:col-span-6 md:col-start-7 space-y-7 text-white/70 text-lg md:text-xl leading-[1.7]">
            <p>
              Every Nocturne edition is designed as a complete editorial experience.
            </p>
            <p>
              Each story unfolds across cinema, photography, publishing and written
              features. Allowing audiences to experience luxury automobiles beyond
              specifications and performance.
            </p>
            <p className="text-white">We don't simply document cars.</p>
            <p className="font-display text-3xl md:text-4xl text-white leading-[1.1]">
              We create stories <span className="italic text-[var(--gb-gold)]">worth driving.</span>
            </p>
          </motion.div>
        </div>
      </section>

      {/* ISSUE PLATE — masthead */}
      <section className="relative border-y border-white/5 overflow-hidden noise">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1610374634235-b51ef357f905?crop=entropy&cs=srgb&fm=jpg&w=2400&q=85')",
          }}
          data-testid="issue-plate-bg"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/40" />
        <div className="absolute inset-0 bg-black/45" />

        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <p className="kbd text-white/60 mb-6">NOCTURNE</p>
            <p className="font-display text-3xl md:text-4xl text-white">{issueNumber}</p>
            <h3 className="font-display text-6xl md:text-8xl text-white italic mt-2 leading-[0.92]">
              {issueTitle}
            </h3>
          </div>
          <div className="md:col-span-5 md:col-start-8 grid grid-cols-5 gap-4 text-white/70">
            {EXPERIENCE.map((e) => (
              <a
                key={e.key}
                href={`#${e.key}`}
                className="kbd hover:text-[var(--gb-gold)] transition-colors"
              >
                {e.label.split(" ")[0]}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* EXPERIENCE GRID — 4 editorial cards (2x2) */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32">
        <motion.p {...reveal} className="overline mb-3">The Experience</motion.p>
        <motion.h2 {...reveal} className="font-display text-4xl md:text-6xl text-white leading-[0.95] mb-16 max-w-3xl">
          A single edition.
          <br />
          <span className="italic text-[var(--gb-gold)]">Four chapters.</span>
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {[
            { key: "film", n: "01", title: "Film", desc: "Experience the cinematic story.", img: "https://images.unsplash.com/photo-1623346483743-b968a27ed34c?w=1600&q=85" },
            { key: "magazine", n: "02", title: "Magazine", desc: "Explore the complete editorial feature.", img: "https://images.unsplash.com/photo-1610374634235-b51ef357f905?w=1600&q=85" },
            { key: "photography", n: "03", title: "Photography", desc: "Browse the curated image collection.", img: "https://images.pexels.com/photos/36237587/pexels-photo-36237587.jpeg?w=1600" },
            { key: "behind", n: "04", title: "Behind the Drive", desc: "Production notes, locations, gear and creative process.", img: "https://images.unsplash.com/photo-1512790182412-b19e6d62bc39?w=1600&q=85" },
          ].map((c) => (
            <motion.a
              {...reveal}
              key={c.key}
              href={`#${c.key}`}
              data-testid={`exp-card-${c.key}`}
              className="group relative overflow-hidden bg-black aspect-[5/4]"
            >
              <div
                className="absolute inset-0 bg-cover bg-center opacity-75 group-hover:opacity-90 group-hover:scale-[1.02] transition-all duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)]"
                style={{ backgroundImage: `url(${c.img})` }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-black/10" />
              <div className="relative z-10 h-full flex flex-col justify-between p-8 md:p-10">
                <div className="flex items-start justify-between">
                  <p className="kbd text-[var(--gb-gold)]">{c.n}</p>
                  <ArrowRight
                    size={16}
                    className="text-[var(--gb-gold)] opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-500"
                  />
                </div>
                <div>
                  <h3 className="font-display text-4xl md:text-5xl text-white leading-[1.05] group-hover:text-[var(--gb-gold)] transition-colors duration-500">
                    {c.title}
                  </h3>
                  <p className="text-white/65 text-sm md:text-base mt-3 leading-relaxed max-w-xs">
                    {c.desc}
                  </p>
                </div>
              </div>
            </motion.a>
          ))}
        </div>
      </section>

      {/* FILM */}
      <section id="film" className="bg-[#050505] py-24 border-y border-white/5 scroll-mt-24">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
          <motion.p {...reveal} className="overline mb-6">Chapter 01 · Film</motion.p>
          <motion.h2 {...reveal} className="font-display text-4xl md:text-6xl text-white mb-12">
            {featured?.title || `${issueNumber} · ${issueTitle}`}
          </motion.h2>
          <motion.div {...reveal} className="aspect-video w-full bg-black border border-white/10 relative overflow-hidden">
            {featured?.youtube_url ? (
              <iframe
                data-testid="featured-youtube-embed"
                src={featured.youtube_url}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="featured-film"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white/50">
                <Play size={48} />
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* MAGAZINE */}
      <section id="magazine" className="py-32 max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-12 items-center scroll-mt-24">
        <motion.div {...reveal} className="md:col-span-6">
          <p className="overline mb-6">Chapter 02 · Magazine</p>
          <h2 className="font-display text-5xl md:text-6xl text-white leading-[1.05]">
            A flippable
            <br />
            <span className="italic text-[var(--gb-gold)]">editorial issue.</span>
          </h2>
          <p className="text-white/70 text-base md:text-lg max-w-md mt-8 leading-relaxed">
            Behind the drive, photography portfolios and editorial features. Designed
            like a printed magazine, optimized for screen.
          </p>
          <button
            data-testid="nocturne-subscribe-cta"
            onClick={() => open("nocturne-page")}
            className="gb-btn-primary mt-10"
          >
            Download Latest Magazine <ArrowRight size={14} />
          </button>
        </motion.div>
        <motion.div {...reveal} className="md:col-span-6">
          <div className="aspect-[4/5] w-full relative overflow-hidden noise">
            <img
              src={featured?.cover_image_url || "https://images.unsplash.com/photo-1610374634235-b51ef357f905?w=1200&q=90"}
              alt="Nocturne cover"
              className="w-full h-full object-cover"
            />
            <div className="absolute top-6 left-6">
              <p className="kbd text-white">NOCTURNE · {issueNumber.toUpperCase()}</p>
            </div>
            <div className="absolute bottom-6 left-6 right-6">
              <p className="font-display text-2xl text-white italic">{issueTitle}</p>
              <div className="gb-divider my-3" />
              <p className="kbd text-white/60">A GOOD BANDIT PUBLICATION</p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* PHOTOGRAPHY */}
      <section id="photography" className="bg-[#050505] py-24 border-t border-white/5 scroll-mt-24">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 mb-10">
          <motion.p {...reveal} className="overline mb-3">Chapter 03 · Photography</motion.p>
          <motion.h2 {...reveal} className="font-display text-4xl md:text-5xl text-white">
            A curated image collection
          </motion.h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-1">
          {(featured?.gallery_image_urls || [
            "https://images.unsplash.com/photo-1610374634235-b51ef357f905?w=900&q=85",
            "https://images.unsplash.com/photo-1623346483743-b968a27ed34c?w=900&q=85",
            "https://images.unsplash.com/photo-1578245600656-e8fe67a2b5f7?w=900&q=85",
            "https://images.pexels.com/photos/36237587/pexels-photo-36237587.jpeg?w=900",
            "https://images.pexels.com/photos/326259/pexels-photo-326259.jpeg?w=900",
            "https://images.pexels.com/photos/36427283/pexels-photo-36427283.jpeg?w=900",
            "https://images.unsplash.com/photo-1599912027667-755b68b4dd3b?w=900&q=85",
            "https://images.unsplash.com/photo-1512790182412-b19e6d62bc39?w=900&q=85",
          ])
            .slice(0, 8)
            .map((url, i) => (
              <motion.div key={i} {...reveal} className="aspect-square overflow-hidden group bg-black">
                <img
                  src={url}
                  alt=""
                  className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
                />
              </motion.div>
            ))}
        </div>
      </section>

      {/* BEHIND THE DRIVE */}
      <section id="behind" className="border-t border-white/5 scroll-mt-24">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-32 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <motion.div {...reveal} className="md:col-span-6">
            <img
              src="https://images.unsplash.com/photo-1512790182412-b19e6d62bc39?w=1400&q=85"
              alt="behind the drive"
              className="w-full"
            />
          </motion.div>
          <motion.div {...reveal} className="md:col-span-5 md:col-start-8">
            <p className="overline mb-6">Chapter 04 · Behind the Drive</p>
            <h2 className="font-display text-4xl md:text-5xl text-white leading-[1.1]">
              The location, the lens,
              <br />
              <span className="italic text-[var(--gb-gold)]">the machine.</span>
            </h2>
            <p className="text-white/65 mt-8 leading-relaxed">
              Production notes, locations, gear and creative process.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ISSUES ARCHIVE */}
      <section className="py-24 border-t border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
          <motion.p {...reveal} className="overline mb-3">The Archive</motion.p>
          <motion.h2 {...reveal} className="font-display text-4xl md:text-5xl text-white mb-12">
            Past Issues
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(editions.length
              ? editions
              : [
                  {
                    title: issueTitle,
                    slug: "issue-01",
                    cover_image_url: "https://images.unsplash.com/photo-1610374634235-b51ef357f905?w=900&q=85",
                    release_date: "2026",
                  },
                ])
              .map((e, i) => (
                <motion.div
                  key={e.id || i}
                  {...reveal}
                  className="gb-card group"
                  data-testid={`edition-card-${e.slug}`}
                >
                  <div className="aspect-[3/4] overflow-hidden">
                    <img
                      src={e.cover_image_url || "https://images.unsplash.com/photo-1610374634235-b51ef357f905?w=900&q=85"}
                      alt={e.title}
                      className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                    />
                  </div>
                  <div className="p-6">
                    <p className="kbd mb-2">
                      ISSUE {String(i + 1).padStart(2, "0")} · {(e.release_date || "").slice(0, 4)}
                    </p>
                    <h3 className="font-display text-2xl text-white italic">{e.title}</h3>
                    <p className="text-sm text-white/55 mt-2 line-clamp-2">{e.summary}</p>
                  </div>
                </motion.div>
              ))}
          </div>
        </div>
      </section>

      {/* IMPACT CROSS-LINK */}
      <section className="border-t border-white/5 bg-black">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-24 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <motion.div {...reveal} className="md:col-span-7">
            <p className="overline mb-4">Also from the studio</p>
            <h3 className="font-display text-3xl md:text-5xl text-white leading-[1]">
              Some stories build <span className="italic text-[var(--gb-gold)]">desire.</span>
              <br />Others build <span className="italic text-white/60">movements.</span>
            </h3>
          </motion.div>
          <motion.div {...reveal} className="md:col-span-4 md:col-start-9">
            <p className="text-white/60 text-sm leading-relaxed">Good Bandit / Impact is where strategy, growth and fundraising meet the studio's creative practice.</p>
            <Link to="/impact" className="gb-btn-outline mt-6 inline-flex text-[10px]">Explore Impact <ArrowRight size={12} /></Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
